import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useSite } from "../../context/SiteContext";

const HERO_IMG = "https://images.unsplash.com/photo-1562362898-d1a9d124fd77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxkYXJrJTIwZ290aGljJTIwYXJjaGl0ZWN0dXJlJTIwZHJhbWF0aWMlMjBtb29keXxlbnwwfHx8fDE3OTE2Nzc5NDJ8MA&ixlib=rb-4.1.0&q=85";

const lineVariant = {
  hidden: { y: "110%" },
  shown: { y: 0, transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = () => {
  const { site } = useSite();
  const s = site?.settings || {};
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const [time, setTime] = useState("");

  useEffect(() => {
    const tick = () => setTime(new Date().toUTCString().slice(17, 25) + " UTC");
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, []);

  const words = (s.site_title || "The Obsidian Vault").split(" ");

  return (
    <section ref={ref} className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
            className="font-mono text-xs tracking-[0.35em] uppercase text-[#581313] mb-8"
            data-testid="hero-eyebrow">
            EST. MMXXVI — PERSONAL HQ — {time}
          </motion.p>
          <motion.h1
            initial="hidden" animate="shown"
            variants={{ shown: { transition: { staggerChildren: 0.14, delayChildren: 0.15 } } }}
            className="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#ede8e1]"
            data-testid="hero-manifesto-text">
            {words.map((w, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span variants={lineVariant} className="block">{w}</motion.span>
              </span>
            ))}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-lg font-light italic text-[#a8a29e] leading-relaxed"
            data-testid="hero-tagline">
            {s.tagline}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap gap-4">
            <a href="#sections" data-testid="hero-enter-archive-button"
              className="bg-[#581313] hover:bg-[#581313] text-[#ede8e1] border border-[#581313]/40 font-mono text-xs uppercase tracking-widest px-6 py-3 transition-colors">
              Enter the Archive
            </a>
            <a href="#contact" data-testid="hero-write-keeper-button"
              className="bg-[#14151b] hover:bg-[#232530] text-[#ede8e1] border border-white/10 font-mono text-xs uppercase tracking-widest px-6 py-3 transition-colors">
              Write to the Keeper
            </a>
          </motion.div>
        </div>
        <div className="lg:col-span-5 relative hidden lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="image-frame arch-top overflow-hidden">
            <motion.img src={HERO_IMG} alt="Gothic cathedral facade" style={{ y }}
              className="w-full h-[520px] object-cover grayscale-[35%] contrast-125 arch-top"
              data-testid="hero-image" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070709]/80 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase text-[#a8a29e]" data-testid="hero-coordinates">
              {s.location_label || "51.5072° N, 0.1276° W — LONDON"}
            </div>
            <div className="absolute top-4 right-4 flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#581313]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#581313] flicker" /> LIVE
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
